import React, { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Fab from '@mui/material/Fab';
import Tooltip from '@mui/material/Tooltip';
import { ArrowUpToLine } from 'lucide-react';

import Page from 'src/components/page/PageV2';
import Provider from 'src/pages/landlord/conversations/context/Provider';
import KeyListener from 'src/pages/landlord/conversations/components/key-listener';
import NotificationAlert from 'src/pages/landlord/conversations/components/NotificationAlert';
import Header from 'src/pages/landlord/conversations/components/header';
import StageTabs from 'src/pages/landlord/conversations/components/navigation';
import List from 'src/pages/landlord/conversations/components/list';
import Toolbar from 'src/pages/landlord/conversations/components/toolbar';
import ConversationsHotkeysFooter from 'src/pages/landlord/conversations/components/hotkeys';
import MobileMenu from 'src/pages/landlord/conversations/components/mobile-menu';

import styles from 'src/pages/landlord/conversations/ConversationsInboxPage.module.css';

export default function ConversationsPage() {
    const [showScrollTop, setShowScrollTop] = useState(false);
    const [isScrolling, setIsScrolling] = useState(false);
    const [isHovering, setIsHovering] = useState(false);

    useEffect(() => {
        let scrollTimer: NodeJS.Timeout;

        const handleScroll = () => {
            const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
            const shouldShow = scrollTop > 300;

            setShowScrollTop(shouldShow);
            setIsScrolling(true);

            // Hide button after user stops scrolling for 2 seconds
            clearTimeout(scrollTimer);
            scrollTimer = setTimeout(() => {
                setIsScrolling(false);
            }, 2000);
        };

        window.addEventListener('scroll', handleScroll);
        return () => {
            window.removeEventListener('scroll', handleScroll);
            clearTimeout(scrollTimer);
        };
    }, []);

    const scrollToTop = () => {
        window.scrollTo({
            top: 0,
            behavior: 'smooth'
        });
    };

    return (
        <Page title="Reffie | Inbox">
            <Provider>
                <KeyListener />

                {/* NOTE: for whatever reason, Box does not set the display: flex property (uses webkit prefix) */}
                <Box flex={1} className="conversations-page" flexDirection="column" flexWrap="nowrap" sx={{
                    minHeight: '100vh',
                    display: 'flex'
                }}
                >
                    <header>
                        <NotificationAlert />
                        <Header searchScope="inbox" />
                        <StageTabs />
                    </header>

                    {/* flex: 1 is necessary here for the sticky footer trick */}
                    <Box flex={1} component="main" flexWrap="nowrap">
                        <List />
                        <Toolbar />
                        <MobileMenu />
                    </Box>
                    <ConversationsHotkeysFooter />
                </Box>

                {/* Scroll to Top Button */}
                {showScrollTop && (
                    <Fab
                        onClick={scrollToTop}
                        size="medium"
                        onMouseEnter={() => setIsHovering(true)}
                        onMouseLeave={() => setIsHovering(false)}
                        className={`${styles.scrollToTopButton} ${(isScrolling || isHovering) ? styles.visible : ''}`}
                    >
                        <Tooltip title="Scroll to top">
                            <ArrowUpToLine color="white" />
                        </Tooltip>
                    </Fab>
                )}

                {/* Invisible hover area */}
                {showScrollTop && !isScrolling && !isHovering && (
                    <div
                        onMouseEnter={() => setIsHovering(true)}
                        className={styles.hoverArea}
                    />
                )}
            </Provider>
        </Page>
    );
}
